// src/components/shell/LanguageSwitcher.js
// Bascule de langue FR / EN. Deux rendus :
//  - LanguageSwitcher (défaut) : deux pilules à plat, pour l'intérieur des menus.
//  - LanguageFlagSwitcher      : drapeau rond + menu déroulant, exposé dans la
//    barre du haut à côté du bouton thème (même repère visuel que fullmargin.net).
// Source unique : setAppLanguage (i18n.js) — garde les clés localStorage
// synchronisées et met à jour <html lang>.
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import i18n, { setAppLanguage } from "../../i18n";

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
];

// Mêmes visuels que le site fullmargin.net : drapeau FR et drapeau US.
const FLAGS = {
  fr: { short: "FR", full: "Français", url: "https://flagcdn.com/w40/fr.png" },
  en: { short: "EN", full: "English", url: "https://flagcdn.com/w40/us.png" },
};

function useCurrentLang() {
  const { i18n: i18next } = useTranslation();
  const code = (i18next?.language || i18n.language || "fr").slice(0, 2);
  return code === "en" ? "en" : "fr";
}

export function LanguageFlagSwitcher({ className = "", size = 40 }) {
  const current = useCurrentLang();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const select = (code) => {
    setAppLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={FLAGS[current].full}
        aria-haspopup="menu"
        aria-expanded={open}
        title={FLAGS[current].full}
        style={{ height: size, width: size }}
        className="grid place-items-center rounded-full bg-white/80 dark:bg-[#181a2e]/80 backdrop-blur ring-1 ring-slate-200 dark:ring-white/10 shadow-sm hover:ring-violet-300 dark:hover:ring-violet-400/40 transition"
      >
        <img
          src={FLAGS[current].url}
          alt={FLAGS[current].full}
          width={24}
          height={24}
          className="h-6 w-6 rounded-full object-cover ring-1 ring-black/10"
          loading="lazy"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 z-[70] min-w-[170px] overflow-hidden rounded-xl bg-white dark:bg-[#181a2e] shadow-xl ring-1 ring-slate-200 dark:ring-white/10 py-1"
        >
          {LANGS.map((l) => {
            const active = current === l.code;
            return (
              <button
                key={l.code}
                type="button"
                role="menuitem"
                onClick={() => select(l.code)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  active
                    ? "font-bold text-violet-600 dark:text-violet-300"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/5"
                }`}
              >
                <img
                  src={FLAGS[l.code].url}
                  alt=""
                  width={22}
                  height={22}
                  className="h-[22px] w-[22px] shrink-0 rounded-full object-cover ring-1 ring-black/10"
                  loading="lazy"
                />
                <span className="flex-1 text-left">{FLAGS[l.code].full}</span>
                {active && <Check size={16} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function LanguageSwitcher({ className = "" }) {
  const { i18n: i18next } = useTranslation();
  const current = (i18next?.language || i18n.language || "fr").slice(0, 2);

  return (
    <div
      role="group"
      aria-label="Langue / Language"
      className={
        "inline-flex items-center gap-1 rounded-xl border border-black/10 bg-white/60 p-1 dark:border-white/10 dark:bg-white/[0.05] " +
        className
      }
    >
      {LANGS.map((l) => {
        const active = current === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setAppLanguage(l.code)}
            aria-pressed={active}
            className={[
              "rounded-lg px-2.5 py-1 text-xs font-semibold transition",
              active
                ? "bg-violet-600 text-white"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white",
            ].join(" ")}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}
